import React from 'react';
import { ArrowUpDown, ArrowUp, ArrowDown } from 'lucide-react';
import { TableColumn, TableProps } from './types';

export type { TableColumn } from './types';

export const Table: React.FC<TableProps> = ({
  columns,
  data,
  className = '',
  sortField = null,
  sortDirection = null,
  onSort
}) => {
  const handleSort = (column: TableColumn) => {
    if (column.sortable && onSort) {
      const field = column.sortField || column.accessor;
      onSort(field);
    }
  };

  const getSortIcon = (column: TableColumn) => {
    if (!column.sortable) return null;

    const field = column.sortField || column.accessor;
    if (sortField === field) {
      if (sortDirection === 'asc') return <ArrowUp className="h-4 w-4" />;
      if (sortDirection === 'desc') return <ArrowDown className="h-4 w-4" />;
    }
    return <ArrowUpDown className="h-4 w-4 opacity-40" />;
  };

  return (
    <div className={`overflow-x-auto ${className}`}>
      <table className="w-full table-auto min-w-full">
        <thead>
          <tr className="border-b border-gray-100 dark:border-white/[0.05]">
            {columns.map((column, index) => (
              <th
                key={index}
                className={`px-5 py-3 font-medium text-gray-500 text-start text-sm dark:text-gray-400 ${
                  column.sortable ? 'cursor-pointer select-none hover:bg-gray-50 dark:hover:bg-white/[0.02] transition-colors' : ''
                } ${column.className || ''}`}
                onClick={() => handleSort(column)}
              >
                <div className="flex items-center gap-2">
                  <span>{column.header}</span>
                  {getSortIcon(column)}
                </div>
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100 dark:divide-white/[0.05]">
          {data.length === 0 ? (
            <tr>
              <td
                colSpan={columns.length}
                className="px-5 py-8 text-center text-sm text-gray-500 dark:text-gray-400"
              >
                No data available
              </td>
            </tr>
          ) : (
            data.map((row, rowIndex) => (
              <tr
                key={rowIndex}
              >
                {columns.map((column, colIndex) => (
                  <td
                    key={colIndex}
                    className="px-5 py-3"
                  >
                    {column.render
                      ? column.render(row[column.accessor], row)
                      : row[column.accessor]}
                  </td>
                ))}
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
};
